import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { FaCircle, FaCrown, FaPlay } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { RouteName } from "constant/routeNames";
import HeroSection from "components/skeleton/videoDetailsSkeleton/HeroSection";
import { useSelector } from "react-redux";
import AddToFaavVideo from "./AddToFaavVideo";
import VideoShareButton from "./VideoShareButton";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { Helmet } from "react-helmet-async";
import { Notification } from "constant/notifications";
import { Modal } from "antd";
import SubscriptionService from "services/subscription/subscriptionService";
import GeneralServices from "services/General/GeneralServices";
import VideoService from "services/video/VideoService";
import { formatDurationHrAndMins } from "utils/timeFunctions";

function VideoDetailHeader({ video, isLoading, slug, playerPage }: any) {
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  const [movie, setMovie] = useState<any>();
  const [tvSeries, setTvSeries] = useState<any>();
  const BaseUrl = window.location.origin;
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;
  const currentUrl = BaseUrl + window.location.pathname;
  const [subscriptionEnabled, setSubscriptionEnabled] = useState<any>();
  // The in-progress watch entry for this exact movie/series, if any - lets
  // the hero button show "Resume - X left" instead of a generic "Watch Now",
  // and resumes playback (and, for a series, the actual last-watched
  // episode) instead of always starting over from the beginning/episode 1.
  const [resumeItem, setResumeItem] = useState<any>(null);

  // ----- Background preview (HLS) -----
  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [startPreview, setStartPreview] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewDone, setPreviewDone] = useState(false);
  const [heroInView, setHeroInView] = useState(true);
  // preview stream comes from `video.url` when source is an m3u8 (HLS) playlist
  const previewUrl: string | undefined =
    video?.source === "m3u8" ? video?.url : undefined;
  const [isBuy, setIsBuy] = useState<boolean>();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // End the preview: fade out, pause, and fall back to the poster image
  const endPreview = () => {
    setPreviewPlaying(false); // fade video out, revealing the image
    setPreviewDone(true);
    videoRef.current?.pause();
    // unmount/destroy the stream after the fade completes
    setTimeout(() => setStartPreview(false), 1000);
  };

  useEffect(() => {
    GeneralServices.getAllConfigData()
      .then((res) => {
        const SubsStatus = res?.data?.filter(
          (item: any) =>
            item?.title === "subscription_enabled" && item?.value !== null,
        );
        if (SubsStatus?.[0]?.value === "1") {
          setSubscriptionEnabled(SubsStatus[0]?.value);
        }
      })
      .catch((e) => {});
  }, []);

  // check content subscribe or not
  const checkIsSubscribe = async () => {
    // plan_id 1 = free content, no access check needed
    if (video?.plan_id === 1) {
      setIsBuy(true);
      return;
    }

    // plan_id 2 = paid content — only check access for logged-in users
    if (video?.plan_id === 2) {
      if (!loggedInUser) {
        setIsBuy(false);
        return;
      }

      try {
        setIsFetching(true);
        const res = await SubscriptionService.checkContentAccessBySlug(
          video?.slug,
        );
        if (res?.status) {
          setIsBuy(res?.data?.has_access);
        }
      } catch {
      } finally {
        setIsFetching(false);
      }
    }
  };

  useEffect(() => {
    checkIsSubscribe();
  }, [video, loggedInUser]);

  // Kick off the preview ~3.5s after the page loads
  useEffect(() => {
    if (!previewUrl) return;
    const t = setTimeout(() => setStartPreview(true), 3500);
    return () => clearTimeout(t);
  }, [previewUrl]);

  // Attach the HLS stream once the preview should start
  useEffect(() => {
    if (!startPreview || !previewUrl) return;
    const el = videoRef.current;
    if (!el) return;

    let hls: Hls | undefined;
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari / native HLS
      el.src = previewUrl;
    } else if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        capLevelToPlayerSize: false, // don't downscale quality to element size
        startLevel: -1,
      });
      hls.loadSource(previewUrl);
      hls.attachMedia(el);
      // Pick the highest-quality rendition for the preview
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (hls && hls.levels.length) {
          hls.currentLevel = hls.levels.length - 1;
        }
      });
    }
    el.play().catch(() => {});

    return () => {
      hls?.destroy();
    };
  }, [startPreview, previewUrl]);

  // Track whether the hero is on screen
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.intersectionRatio >= 0.5),
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoading]);

  // Show the preview for 20 seconds, then return to the image
  useEffect(() => {
    if (!previewPlaying || previewDone) return;
    const t = setTimeout(() => endPreview(), 20000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPlaying, previewDone]);

  // Stop the preview as soon as the hero scrolls out of view
  useEffect(() => {
    if (!startPreview || previewDone) return;
    if (!heroInView) endPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroInView, startPreview, previewDone]);

  useEffect(() => {
    if (video?.seasons) {
      setTvSeries(video);
    } else {
      setMovie(video);
    }
  }, [video]);

  useEffect(() => {
    const targetSlug = video?.seasons ? video?.slug : video?.slug;
    if (!loggedInUser || !targetSlug) {
      setResumeItem(null);
      return;
    }
    VideoService.getUserLastWatch()
      .then((res: any) => {
        const all = [
          ...(res?.watching_movies ?? []),
          ...(res?.watching_tv_shows ?? []),
        ];
        const match = all.find((item: any) => item.slug === targetSlug);
        // Don't offer to "resume" something that's effectively finished -
        // once there's under 100s left, treat it the same as not-started.
        const isResumable =
          match && match.duration - match.currentTime >= 100;
        setResumeItem(isResumable ? match : null);
      })
      .catch(() => {
        setResumeItem(null);
      });
  }, [loggedInUser, video?.slug, video?.seasons]);

  const setDataToPlayer = async () => {
    if (tvSeries?.first_episode) {
      const stream =
        resumeItem?.stream ?? tvSeries?.first_episode.stream_key;
      navigate(
        RouteName.WatchTVSeries.replace(":slug", tvSeries?.slug).replace(
          ":stream",
          stream,
        ),
        resumeItem ? { state: { currentTime: resumeItem.currentTime } } : undefined,
      );
    } else {
      navigate(
        RouteName.WatchMovies.replace(":slug", movie?.slug),
        resumeItem ? { state: { currentTime: resumeItem.currentTime } } : undefined,
      );
    }
  };
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleBack = () => {
    window.scrollTo(0, 0);

    if (window.history.length !== 2) {
      window.scrollTo(0, 0);
      if (playerPage) {
        navigate(RouteName.Video.replace("/*", ""));
      } else {
        navigate(-1);
      }
    } else {
      navigate(RouteName.Video.replace("/*", ""));
    }
  };

  //subscription function
  const handleSubscription = () => {
    setIsModalOpen(true);
  };

  const metaDescription =
    video?.description?.length > 170
      ? `${video.description.slice(0, 160).trim()}...`
      : video?.description;

  return (
    <>
      <Helmet>
        <title>{video?.seo_title ? `${video?.seo_title}` : "AYOZAT"}</title>
        <meta name="title" content={`${video?.seo_title}`} />
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={video?.focus_keyword} />

        <link rel="canonical" href={`${FE_URL}/video/info/${video?.slug}`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${video?.seo_title}`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={video?.poster_url || video?.poster || video?.thumbnail} />
        <meta name="twitter:site" content="AYOZAT" />
        <meta
          name="twitter:url"
          content={`${FE_URL}/profile/${video?.description}`}
        />

        <meta property="og:title" content={`${video?.seo_title}`} />
        <meta property="og:image" content={video?.poster_url || video?.poster || video?.thumbnail} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:url"
          content={`${FE_URL}/video/info/${video?.slug}`}
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div>
        {!isLoading && (
          <div
            ref={heroRef}
            className="relative flex h-[700px] w-full overflow-hidden max-sm:h-[640px] max-sm:w-full"
          >
            {/* Key-art backdrop, anchored to the right */}
            <div
              className="absolute inset-0 bg-no-repeat bg-cover"
              style={{
                backgroundImage: `url(${
                  video?.poster_url || video?.poster || video?.thumbnail
                })`,
                backgroundPosition: "right center",
                zIndex: -1,
              }}
            ></div>

            {/* Background video preview — fades in a few seconds after load */}
            {startPreview && previewUrl && (
              <video
                ref={videoRef}
                muted
                loop
                autoPlay
                playsInline
                onPlaying={() => setPreviewPlaying(true)}
                className="absolute inset-0 object-cover w-full h-full transition-opacity duration-1000 ease-in-out"
                style={{
                  zIndex: -1,
                  opacity: previewPlaying ? 1 : 0,
                  objectPosition: "right center",
                }}
              />
            )}

            {/* Cinematic scrims — fade into dark on the left / bottom */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-black via-black/85 to-transparent max-sm:from-black max-sm:via-black/90 max-sm:to-black/40"></div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black via-black/30 to-transparent"></div>

            {/* Back button */}
            <button
              className="absolute z-20 flex items-center justify-center w-10 h-10 transition-all duration-300 rounded-full left-8 top-8 bg-white/90 backdrop-blur-md hover:scale-105 hover:bg-white max-sm:left-4 max-sm:top-5"
              onClick={handleBack}
            >
              <AiOutlineArrowLeft />
            </button>

            {/* Content */}
            <div className="relative z-10 flex items-center w-full h-full">
              <div className="ml-10 flex max-w-[640px] flex-col gap-6 max-sm:ml-5 max-sm:max-w-full max-sm:pr-5 max-lg:max-w-[90%]">
                {/* Eyebrow */}
                <div className="flex flex-col gap-2">
                  <p className="font-rubik text-[13px] font-bold uppercase tracking-[0.25em] text-[var(--red)]">
                    {video?.seasons ? "TV Series" : "Movie"}
                  </p>
                  <h1
                    className="font-oswald text-6xl font-black uppercase leading-[1] tracking-tight text-white drop-shadow-2xl max-sm:text-[34px] max-lg:text-[44px]"
                    dangerouslySetInnerHTML={{ __html: video?.title }}
                  ></h1>
                </div>

                {/* Description */}
                <p
                  className="creator-descrip font-rubik line-clamp-2 max-w-xl text-[15px] font-normal leading-7 text-white/80 max-sm:text-sm"
                  dangerouslySetInnerHTML={{ __html: video?.description }}
                ></p>

                {/* Meta badges */}
                <div className="font-rubik flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-white/85">
                  {video?.imdb_rating &&
                    video?.imdb_rating !== "N/A" &&
                    video?.imdb_rating !== null && (
                      <span className="rounded bg-[#f5c518] px-1.5 py-0.5 text-[11px] font-bold tracking-normal text-black">
                        IMDb {video?.imdb_rating}
                      </span>
                    )}
                  {video?.runtime ? (
                    <>
                      <span className="text-white">
                        {Math.floor(video?.runtime / 60) > 0 &&
                          `${Math.floor(video?.runtime / 60)}h `}
                        {Math.floor(video?.runtime % 60) > 0 &&
                          `${Math.floor(video?.runtime % 60)}min`}
                      </span>
                    </>
                  ) : null}
                  {video?.released_at !== null &&
                  video?.released_at !== "-1" ? (
                    <>
                      <FaCircle className="text-[5px] text-white/50" />
                      <span className="text-white">
                        {new Date(video?.released_at).getFullYear()}
                      </span>
                    </>
                  ) : null}
                </div>

                {/* Genres */}
                <div className="flex flex-wrap gap-2.5">
                  {video?.genres.length > 0 &&
                    video?.genres.map((genre: any, i: number) => (
                      <Link
                        key={i}
                        to={`${RouteName.VideoDiscoverRoute}/${genre?.alt_name}`}
                      >
                        <p className="font-rubik rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white backdrop-blur-sm transition-all duration-200 hover:border-[var(--red)] hover:bg-[var(--red)] hover:text-white">
                          {genre?.name}
                        </p>
                      </Link>
                    ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 mt-2 max-sm:gap-3">
                  {isFetching ? (
                    <>
                      <div className="animate-pulse w-[200px] px-8 py-3.5 bg-[#1a1919] h-12 rounded-sm"></div>
                    </>
                  ) : (
                    <>
                      {subscriptionEnabled === "1" && !isBuy ? (
                        <button
                          onClick={handleSubscription}
                          className="flex items-center gap-3 rounded-full bg-[var(--red)] px-8 py-3.5 text-[16px] font-bold text-white shadow-lg shadow-[var(--red)]/30 transition-all hover:scale-105 hover:brightness-110 sm:text-[18px]"
                        >
                          <FaCrown className="text-[20px]" />
                          Subscribe to Watch
                        </button>
                      ) : (
                        <>
                          {video && video?.allowed_to_watch ? (
                            <button
                              onClick={setDataToPlayer}
                              className="flex items-center gap-4 group"
                            >
                              <span className="flex items-center justify-center w-16 h-16 text-black transition-transform duration-300 bg-white rounded-full shadow-xl group-hover:scale-105 max-sm:h-14 max-sm:w-14">
                                <FaPlay className="ml-1 text-xl" />
                              </span>
                              <span className="font-rubik text-[17px] font-bold text-white">
                                {resumeItem
                                  ? `Resume • ${formatDurationHrAndMins(
                                      Math.max(
                                        0,
                                        resumeItem.duration - resumeItem.currentTime,
                                      ),
                                    )} left`
                                  : "Watch Now"}
                              </span>
                            </button>
                          ) : (
                            <p className="p-4 text-white border-2 border-white border-solid rounded-md">
                              {Notification.geoLocationNotAllowedText}
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Secondary glassy buttons */}
                  {loggedInUser && (
                    <div className="flex items-center justify-center w-12 h-12 text-white transition-all duration-300 border rounded-lg border-white/15 bg-white/10 backdrop-blur-md hover:bg-white/20 max-sm:h-11 max-sm:w-11">
                      <AddToFaavVideo
                        seasons={video?.seasons}
                        id={video?.id}
                        title={video?.title}
                        movie={movie}
                        tvSeries={tvSeries}
                        Icon={true}
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-center w-12 h-12 text-white transition-all duration-300 border rounded-lg border-white/15 bg-white/10 backdrop-blur-md hover:bg-white/20 max-sm:h-11 max-sm:w-11">
                    <VideoShareButton shareUrl={currentUrl} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {isLoading && <HeroSection />}

        {/* open the subscription plan modal */}
        <Modal
          title={null}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          centered
          className="premium-modal"
          styles={{
            content: { backgroundColor: "#111", border: "1px solid #333" },
          }}
        >
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="mb-4 rounded-full bg-[var(--red)]/10 p-4">
              <FaCrown className="text-4xl text-[var(--red)]" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-white">
              Premium Content
            </h3>
            <p className="mb-6 text-gray-400">
              This is exclusively available for premium subscribers. Upgrade
              your plan to gain full access.
            </p>
            <div className="flex w-full gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 rounded-lg border border-gray-600 px-4 py-2 font-semibold text-gray-300 transition hover:bg-[#222]"
              >
                Maybe Later
              </button>
              <Link
                to={RouteName.SubscriptionPlanRoute}
                className="flex flex-1 items-center justify-center rounded-lg bg-[var(--red)] px-4 py-2 font-semibold text-white shadow-md transition hover:opacity-90"
              >
                View Plans
              </Link>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default VideoDetailHeader;
